const US_COUNTRY_LABELS = new Set(["USA", "United States", "US"])
const CA_COUNTRY_LABELS = new Set(["Canada"])

const EXCLUDED_NON_CITY_TYPES = new Set([
  "administrative_area_level_1",
  "administrative_area_level_2",
  "administrative_area_level_3",
  "country"
])

const US_CA_COUNTRY_RESTRICTION = { country: ["us", "ca"] }

/** Leading digit run length (US ZIP / ZIP+4 typing). */
export const usZipDigitCount = (input) => {
  const match = (input || "").trim().match(/^(\d+)/)
  return match ? match[1].length : 0
}

/** User is searching by postal code (US digits or Canadian letter-digit pattern). */
export const isPostalCodeInput = (input) => {
  const trimmed = (input || "").trim()
  if (!trimmed) return false
  if (/^\d/.test(trimmed)) return true
  // Canadian: letter + digit (e.g. K1, K1A, K1A 1A1)
  if (/^[A-Za-z]\d/.test(trimmed)) return true
  return false
}

/** Only call Google once input is long enough to be a meaningful postal search. */
export const shouldFetchHomepageLocationPredictions = (input) => {
  const trimmed = (input || "").trim()
  if (!trimmed) return false

  if (/^\d/.test(trimmed)) {
    // US ZIP: search after more than 4 digits (5+)
    return usZipDigitCount(trimmed) >= 5
  }

  if (/^[A-Za-z]\d/.test(trimmed)) {
    // Canadian FSA is 3 chars (A1A); no valid codes shorter than that
    const normalized = trimmed.replace(/\s/g, "")
    return normalized.length >= 3 && /^[A-Za-z]\d[A-Za-z]/i.test(normalized.slice(0, 3))
  }

  return trimmed.length >= 2
}

export const isHomepagePostalSearch = (input) => {
  const trimmed = (input || "").trim()
  if (!trimmed || !shouldFetchHomepageLocationPredictions(trimmed)) return false
  return isPostalCodeInput(trimmed)
}

/** Google returns poor/no zip results when locality and postal_code are requested together for numeric input. */
export const getHomepageLocationAutocompleteOptions = (input) => {
  const base = { componentRestrictions: US_CA_COUNTRY_RESTRICTION }

  if (isHomepagePostalSearch(input)) {
    // (regions) includes postal_code; works more reliably than postal_code-only for US digits
    return { ...base, types: ["(regions)"] }
  }

  return { ...base, types: ["locality"] }
}

export const isPostalCodePlacePrediction = (prediction) => {
  const types = prediction?.types ?? []
  if (types.includes("postal_code") || types.includes("postal_code_prefix")) {
    return true
  }
  const description = prediction?.description || ""
  if (/\b\d{5}(-\d{4})?\b/.test(description)) {
    return true
  }
  return /\b[A-Z]\d[A-Z]\s?\d[A-Z]\d\b/i.test(description)
}

export const isCityPlacePrediction = (prediction) => {
  const types = prediction?.types ?? []
  if (!types.includes("locality") || isPostalCodePlacePrediction(prediction)) {
    return false
  }
  return !types.some((type) => EXCLUDED_NON_CITY_TYPES.has(type))
}

export const isUsOrCanadaPlacePrediction = (prediction) => {
  if (!prediction) return false

  const lastTerm = prediction.terms?.[prediction.terms.length - 1]?.value?.trim() ?? ""
  if (lastTerm && (US_COUNTRY_LABELS.has(lastTerm) || CA_COUNTRY_LABELS.has(lastTerm))) {
    return true
  }

  const description = prediction.description || ""
  if (/,\s*(USA|United States|Canada)\s*$/i.test(description)) {
    return true
  }

  if (isPostalCodePlacePrediction(prediction) && /^[A-Z]{2}$/i.test(lastTerm)) {
    return true
  }

  return false
}

/** Homepage dropdown: US/CA cities or postal codes depending on what the user typed. */
export const filterHomepagePlacePredictions = (predictions, input = "") => {
  if (!predictions?.length) return predictions ?? []
  if (!shouldFetchHomepageLocationPredictions(input)) return []

  if (isHomepagePostalSearch(input)) {
    return predictions.filter(isPostalCodePlacePrediction)
  }

  return predictions.filter(isCityPlacePrediction)
}
